import { FC } from "react";
import {
  faBullseyeArrow,
  faCode,
  faGem,
} from "@fortawesome/pro-regular-svg-icons";
import { faShieldHalved } from "@fortawesome/pro-solid-svg-icons";
import { Group } from "@mantine/core";

import { Confidence as ConfidenceProto } from "@protos/common/v1/common";
import { SupersededRule } from "@protos/issues/v1/issues";
import {
  ALL_LANGUAGES,
  ID_BY_KEY,
  SECURE_DEFAULT_SUBCATEGORY,
} from "@shared/utils";

import { SecureDefaultInfo } from "../SecureDefaultInfo";

import { IconInfo } from "./IconInfo";
import { SupersededRuleInfo } from "./SupersededRuleInfo";

const CONFIDENCE_TO_LABEL = {
  [ConfidenceProto.LOW]: "Low",
  [ConfidenceProto.MEDIUM]: "Medium",
  [ConfidenceProto.HIGH]: "High",
  [ConfidenceProto.UNSPECIFIED]: "Unspecified",
};

export interface SastRuleInfoIconListProps {
  supersededBy?: SupersededRule[];
  confidence?: ConfidenceProto;
  ruleLanguage?: string;
  ruleCategory?: string;
  ruleSubcategory?: string[];
  isPro?: boolean;
}

/**
 *
 * @param
 * @returns list of icon and label pairs in a row. Specifically for use with SelectableSeverityCard component
 */
export const SastRuleInfoIconList: FC<SastRuleInfoIconListProps> = ({
  supersededBy,
  confidence,
  ruleLanguage,
  ruleCategory,
  ruleSubcategory,
  isPro,
}) => {
  // Show the secure default label if secure default UI is enabled and the rule is in the secure default subcategory
  const showSecureDefaultLabel = ruleSubcategory
    ?.map?.((r) => r.toLowerCase())
    .includes(SECURE_DEFAULT_SUBCATEGORY);

  // Show the security label if we are not showing secure default and the rule is in the security category
  const showSecurityLabel =
    !showSecureDefaultLabel && ruleCategory?.toLowerCase() === "security";

  const languageId: string | undefined =
    ruleLanguage && ID_BY_KEY[ruleLanguage];
  const displayLanguage: string | undefined =
    languageId && languageId in ALL_LANGUAGES
      ? ALL_LANGUAGES[languageId].name
      : ruleLanguage;
  const confidenceLabel: string | undefined = confidence
    ? CONFIDENCE_TO_LABEL[confidence]
    : undefined;

  return (
    <Group gap={12}>
      {isPro && (
        <IconInfo
          tooltip="Pro rule"
          label="Pro"
          icon={faGem}
          color="violet.7"
        />
      )}
      {supersededBy && <SupersededRuleInfo supersededBy={supersededBy} />}
      {showSecurityLabel && (
        <IconInfo tooltip="Category" icon={faShieldHalved} label="Security" />
      )}
      {showSecureDefaultLabel && (
        <SecureDefaultInfo fz="xs" newLabelFz="0.8em" />
      )}
      {confidenceLabel && (
        <IconInfo
          tooltip="Confidence"
          icon={faBullseyeArrow}
          label={confidenceLabel}
        />
      )}
      {ruleLanguage && (
        <IconInfo tooltip="Language" icon={faCode} label={displayLanguage} />
      )}
    </Group>
  );
};
